import { useState } from 'react';
import { X, Check, Upload, Link as LinkIcon, Image, Video, Music } from 'lucide-react';
import { api, UserProfile, RoleKey, PortfolioItem } from '../../lib/api';
import { haptic } from '../../lib/telegram';

interface RegistrationWizardProps {
  onComplete: (profile: UserProfile) => void;
  onClose?: () => void;
  initialProfile?: Partial<UserProfile>;
}

const ROLES: Array<{ id: RoleKey; label: string }> = [
  { id: 'listener', label: 'Listener' },
  { id: 'beatmaker', label: 'Beatmaker' },
  { id: 'producer', label: 'Producer' },
  { id: 'designer', label: 'Designer' },
  { id: 'performer', label: 'Performer' },
  { id: 'director', label: 'Director' },
];

export default function RegistrationWizard({ onComplete, onClose, initialProfile }: RegistrationWizardProps) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<Partial<UserProfile>>({
    name: initialProfile?.name || '',
    photo_file_id: initialProfile?.photo_file_id,
    roles: initialProfile?.roles || '',
    city: initialProfile?.city || '',
    styles: initialProfile?.styles || '',
    bio: initialProfile?.bio || '',
    portfolios: initialProfile?.portfolios || [],
  });
  
  const [roles, setRoles] = useState<RoleKey[]>(
    initialProfile?.roles ? String(initialProfile.roles).split(',') as RoleKey[] : []
  );
  
  const [errors, setErrors] = useState<string>('');
  const [tempInput, setTempInput] = useState('');
  const [portfolioType, setPortfolioType] = useState<'link' | 'photo' | 'video' | 'audio' | null>(null);

  const validateAndNext = async () => {
    setErrors('');
    
    // Step 1: Name validation
    if (step === 1) {
      if (!profile.name || profile.name.length < 2 || profile.name.length > 50) {
        setErrors('Імʼя має бути від 2 до 50 символів');
        haptic.light();
        return;
      }
      await api.profile.update({ name: profile.name });
      setStep(2);
      haptic.light();
    }
    
    // Step 2: Photo (optional, skip allowed)
    else if (step === 2) {
      if (profile.photo_file_id) {
        await api.profile.update({ photo_file_id: profile.photo_file_id });
      }
      setStep(3);
      haptic.light();
    }
    
    // Step 3: Roles validation
    else if (step === 3) {
      if (roles.length === 0) {
        setErrors('Оберіть хоча б одну роль');
        haptic.light();
        return;
      }
      await api.profile.update({ roles: roles.join(',') });
      setProfile({ ...profile, roles: roles.join(',') });
      setStep(4);
      haptic.light();
    }
    
    // Step 4: City
    else if (step === 4) {
      const cityValue = tempInput.trim() || 'Не вказано';
      if (tempInput.trim() && (cityValue.length < 2 || cityValue.length > 50)) {
        setErrors('Місто має бути від 2 до 50 символів');
        haptic.light();
        return;
      }
      const updatedProfile = { ...profile, city: cityValue };
      setProfile(updatedProfile);
      await api.profile.update({ city: cityValue });
      
      // Listener shortcut: auto-complete after city
      if (roles.length === 1 && roles[0] === 'listener') {
        await api.profile.update({ styles: 'Не вказано', bio: 'Слухач музики' });
        await api.profile.updatePortfolio([]);
        haptic.success();
        const finalProfile = await api.profile.get();
        onComplete(finalProfile);
        return;
      }
      
      setTempInput('');
      setStep(5);
      haptic.light();
    }
    
    // Step 5: Styles
    else if (step === 5) {
      const stylesValue = tempInput.trim() || 'Не вказано';
      if (tempInput.trim() && (stylesValue.length < 2 || stylesValue.length > 100)) {
        setErrors('Стилі мають бути від 2 до 100 символів');
        haptic.light();
        return;
      }
      setProfile({ ...profile, styles: stylesValue });
      await api.profile.update({ styles: stylesValue });
      setTempInput('');
      setStep(6);
      haptic.light();
    }
    
    // Step 6: Bio
    else if (step === 6) {
      const bioValue = tempInput.trim() || 'Не вказано';
      if (tempInput.trim() && bioValue.length > 250) {
        setErrors('Біо має бути до 250 символів');
        haptic.light();
        return;
      }
      setProfile({ ...profile, bio: bioValue });
      await api.profile.update({ bio: bioValue });
      setTempInput('');
      setStep(7);
      haptic.light();
    }
    
    // Step 7: Portfolio
    else if (step === 7) {
      if (!profile.portfolios || profile.portfolios.length === 0) {
        setErrors('Додайте хоча б один елемент портфоліо');
        haptic.light();
        return;
      }
      await api.profile.updatePortfolio(profile.portfolios);
      haptic.success();
      const finalProfile = await api.profile.get();
      onComplete(finalProfile);
    }
  };

  const hapticLight = () => haptic.light();

  const toggleRole = (roleId: RoleKey) => {
    if (roleId === 'listener') {
      // Listener is exclusive
      if (roles.includes('listener')) {
        setRoles([]);
      } else {
        setRoles(['listener']);
      }
    } else {
      // Selecting other role removes listener
      const newRoles = roles.filter(r => r !== 'listener');
      if (newRoles.includes(roleId)) {
        setRoles(newRoles.filter(r => r !== roleId));
      } else {
        setRoles([...newRoles, roleId]);
      }
    }
    setErrors('');
  };

  const addPortfolioItem = () => {
    if (!portfolioType || !tempInput.trim()) {
      setErrors('Введіть значення для портфоліо');
      hapticLight();
      return;
    }
    
    if ((profile.portfolios?.length || 0) >= 3) {
      setErrors('Максимум 3 елементи портфоліо');
      hapticLight();
      return;
    }

    // Map 'audio' type (used in API) from 'mp3' (used in UI)
    const apiType: PortfolioItem['type'] = portfolioType === 'audio' ? 'audio' : portfolioType;
    const newItem: PortfolioItem = {
      type: apiType,
      value: tempInput.trim(),
    };

    setProfile({
      ...profile,
      portfolios: [...(profile.portfolios || []), newItem],
    });
    setTempInput('');
    setPortfolioType(null);
    setErrors('');
    hapticLight();
  };

  const clearPortfolio = () => {
    setProfile({ ...profile, portfolios: [] });
    setTempInput('');
    setPortfolioType(null);
    hapticLight();
  };

  const handleProfilePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setErrors('');
      hapticLight();

      const uploaded = await api.profile.uploadPortfolioFile(file, 'Фото профілю');

      // Optimistically add to local portfolios so it appears in step 7
      const newPortfolioItem: PortfolioItem = {
        type: uploaded.type as PortfolioItem['type'],
        value: uploaded.url,
        preview_url: uploaded.url,
        cover_url: uploaded.url,
      };

      setProfile((prev) => ({
        ...prev,
        photo_file_id: uploaded.url,
        portfolios: [...(prev.portfolios || []), newPortfolioItem],
      }));
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      setErrors('Не вдалося завантажити фото. Спробуйте ще раз.');
      haptic.error();
    } finally {
      // Reset input so the same file can be selected again if needed
      event.target.value = '';
    }
  };

  const handlePortfolioFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setErrors('');
      hapticLight();

      const uploaded = await api.profile.uploadPortfolioFile(file, 'Портфоліо файл');

      const newItem: PortfolioItem = {
        type: uploaded.type as PortfolioItem['type'],
        value: uploaded.url,
        preview_url: uploaded.url,
        cover_url: uploaded.url,
      };

      setProfile((prev) => ({
        ...prev,
        portfolios: [...(prev.portfolios || []), newItem],
      }));
    } catch (error) {
      console.error('Failed to upload portfolio file:', error);
      setErrors('Не вдалося завантажити файл. Спробуйте ще раз.');
      haptic.error();
    } finally {
      event.target.value = '';
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Імʼя</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Введіть ваше імʼя (2-50 символів)
            </p>
            <input
              type="text"
              value={profile.name || ''}
              onChange={(e) => {
                setProfile({ ...profile, name: e.target.value });
                setErrors('');
              }}
              placeholder="Ваше імʼя"
              className="w-full h-12 px-4 rounded-2xl border backdrop-blur-xl transition-all focus:outline-none focus:border-[#00FF88]/50 mb-4"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
              }}
            />
          </div>
        );

      case 2:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Фото</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Завантажте фото профілю або пропустіть
            </p>
            <div className="flex flex-col gap-3 mb-4">
              <label
                className="h-32 rounded-2xl border backdrop-blur-xl transition-all hover:border-[#00FF88]/50 flex flex-col items-center justify-center gap-2 cursor-pointer"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProfilePhotoChange}
                />
                <Upload className="w-8 h-8" style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                  Завантажити фото
                </span>
                {profile.photo_file_id && (
                  <span style={{ color: 'rgba(0, 255, 136, 0.8)', fontSize: '12px' }}>
                    Фото завантажено
                  </span>
                )}
              </label>
              <button
                onClick={validateAndNext}
                className="h-12 rounded-2xl border backdrop-blur-xl transition-all hover:border-white/30"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                }}
              >
                Пропустити
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Ролі</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Оберіть одну або декілька ролей
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {ROLES.map((role) => {
                const isSelected = roles.includes(role.id);
                return (
                  <button
                    key={role.id}
                    onClick={() => toggleRole(role.id)}
                    className="h-24 rounded-2xl border backdrop-blur-xl transition-all hover:border-[#00FF88]/50 relative"
                    style={{
                      background: isSelected
                        ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.15) 0%, rgba(0, 255, 136, 0.08) 100%)'
                        : 'rgba(255, 255, 255, 0.05)',
                      borderColor: isSelected ? 'rgba(0, 255, 136, 0.4)' : 'rgba(255, 255, 255, 0.12)',
                      color: isSelected ? '#00FF88' : '#FFFFFF',
                      boxShadow: isSelected
                        ? '0 4px 16px rgba(0, 255, 136, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                        : '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    {role.label}
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-5 h-5" style={{ color: '#00FF88' }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Місто</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Введіть ваше місто (необов'язково)
            </p>
            <input
              type="text"
              value={tempInput}
              onChange={(e) => {
                setTempInput(e.target.value);
                setErrors('');
              }}
              placeholder="Ваше місто"
              className="w-full h-12 px-4 rounded-2xl border backdrop-blur-xl transition-all focus:outline-none focus:border-[#00FF88]/50 mb-4"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
              }}
            />
          </div>
        );

      case 5:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Стилі</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Введіть музичні стилі (напр: trap, hyperpop...) (необов'язково)
            </p>
            <input
              type="text"
              value={tempInput}
              onChange={(e) => {
                setTempInput(e.target.value);
                setErrors('');
              }}
              placeholder="trap, hyperpop..."
              className="w-full h-12 px-4 rounded-2xl border backdrop-blur-xl transition-all focus:outline-none focus:border-[#00FF88]/50 mb-4"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
              }}
            />
          </div>
        );

      case 6:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Біо</h2>
            <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Розкажіть про себе (макс 250 символів, необов'язково)
            </p>
            <textarea
              value={tempInput}
              onChange={(e) => {
                setTempInput(e.target.value);
                setErrors('');
              }}
              placeholder="Про мене..."
              rows={5}
              className="w-full px-4 py-3 rounded-2xl border backdrop-blur-xl transition-all focus:outline-none focus:border-[#00FF88]/50 mb-2 resize-none"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                color: '#FFFFFF',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                minHeight: '120px',
              }}
            />
            <div className="mb-4 text-right" style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
              {tempInput.length}/250
            </div>
          </div>
        );

      case 7:
        return (
          <div>
            <h2 className="mb-2" style={{ color: '#FFFFFF' }}>Портфоліо</h2>
            <p className="mb-4" style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
              Додайте від 1 до 3 елементів портфоліо
            </p>
            
            <div className="mb-4 p-3 rounded-2xl border backdrop-blur-xl"
                 style={{
                   background: 'rgba(0, 255, 136, 0.05)',
                   borderColor: 'rgba(0, 255, 136, 0.2)',
                 }}>
              <p style={{ color: '#00FF88', fontSize: '13px' }}>
                Додано: {profile.portfolios?.length || 0}/3
              </p>
            </div>

            {/* Portfolio items list */}
            {(profile.portfolios?.length || 0) > 0 && (
              <div className="mb-4 space-y-2">
                {profile.portfolios?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-xl border backdrop-blur-xl"
                       style={{
                         background: 'rgba(255, 255, 255, 0.05)',
                         borderColor: 'rgba(255, 255, 255, 0.12)',
                       }}>
                    {item.type === 'link' && <LinkIcon className="w-4 h-4" style={{ color: '#00FF88' }} />}
                    {item.type === 'photo' && <Image className="w-4 h-4" style={{ color: '#00FF88' }} />}
                    {item.type === 'video' && <Video className="w-4 h-4" style={{ color: '#00FF88' }} />}
                    {item.type === 'audio' && <Music className="w-4 h-4" style={{ color: '#00FF88' }} />}
                    <span className="flex-1 truncate" style={{ color: '#FFFFFF', fontSize: '13px' }}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Add portfolio item */}
            {(profile.portfolios?.length || 0) < 3 && (
              <>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <button
                    onClick={() => setPortfolioType('link')}
                    className="h-16 rounded-xl border backdrop-blur-xl transition-all flex flex-col items-center justify-center gap-1"
                    style={{
                      background: portfolioType === 'link' ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: portfolioType === 'link' ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 255, 0.12)',
                    }}>
                    <LinkIcon className="w-5 h-5" style={{ color: portfolioType === 'link' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)' }} />
                    <span style={{ color: portfolioType === 'link' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Link</span>
                  </button>
                  <button
                    onClick={() => setPortfolioType('photo')}
                    className="h-16 rounded-xl border backdrop-blur-xl transition-all flex flex-col items-center justify-center gap-1"
                    style={{
                      background: portfolioType === 'photo' ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: portfolioType === 'photo' ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 255, 0.12)',
                    }}>
                    <Image className="w-5 h-5" style={{ color: portfolioType === 'photo' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)' }} />
                    <span style={{ color: portfolioType === 'photo' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Photo</span>
                  </button>
                  <button
                    onClick={() => setPortfolioType('video')}
                    className="h-16 rounded-xl border backdrop-blur-xl transition-all flex flex-col items-center justify-center gap-1"
                    style={{
                      background: portfolioType === 'video' ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: portfolioType === 'video' ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 255, 0.12)',
                    }}>
                    <Video className="w-5 h-5" style={{ color: portfolioType === 'video' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)' }} />
                    <span style={{ color: portfolioType === 'video' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>Video</span>
                  </button>
                  <button
                    onClick={() => setPortfolioType('audio')}
                    className="h-16 rounded-xl border backdrop-blur-xl transition-all flex flex-col items-center justify-center gap-1"
                    style={{
                      background: portfolioType === 'audio' ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      borderColor: portfolioType === 'audio' ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 255, 0.12)',
                    }}>
                    <Music className="w-5 h-5" style={{ color: portfolioType === 'audio' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)' }} />
                    <span style={{ color: portfolioType === 'audio' ? '#00FF88' : 'rgba(255, 255, 255, 0.6)', fontSize: '10px' }}>MP3</span>
                  </button>
                </div>

                {portfolioType && (
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={tempInput}
                      onChange={(e) => {
                        setTempInput(e.target.value);
                        setErrors('');
                      }}
                      placeholder={`Введіть ${portfolioType === 'link' ? 'посилання' : 'URL'}`}
                      className="flex-1 h-12 px-4 rounded-2xl border backdrop-blur-xl transition-all focus:outline-none focus:border-[#00FF88]/50"
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                      }}
                    />
                    <button
                      onClick={addPortfolioItem}
                      className="w-12 h-12 rounded-2xl border backdrop-blur-xl transition-all hover:border-[#00FF88]/50 flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15) 0%, rgba(0, 255, 136, 0.08) 100%)',
                        borderColor: 'rgba(0, 255, 136, 0.4)',
                        boxShadow: '0 4px 16px rgba(0, 255, 136, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Check className="w-5 h-5" style={{ color: '#00FF88' }} />
                    </button>
                  </div>
                )}
                <div className="mt-3">
                  <label
                    className="inline-flex items-center justify-center px-4 h-10 rounded-2xl border backdrop-blur-xl transition-all hover:border-[#00FF88]/50 cursor-pointer"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                      color: '#FFFFFF',
                      fontSize: '13px',
                    }}
                  >
                    <input
                      type="file"
                      className="hidden"
                      onChange={handlePortfolioFileChange}
                    />
                    <Upload className="w-4 h-4 mr-2" />
                    Завантажити файл у портфоліо
                  </label>
                </div>
              </>
            )}

            {(profile.portfolios?.length || 0) > 0 && (
              <button
                onClick={clearPortfolio}
                className="w-full h-10 rounded-2xl border backdrop-blur-xl transition-all hover:border-red-500/50"
                style={{
                  background: 'rgba(255, 0, 0, 0.05)',
                  borderColor: 'rgba(255, 0, 0, 0.2)',
                  color: 'rgba(255, 100, 100, 0.9)',
                }}
              >
                Очистити
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-2xl h-[90vh] rounded-t-3xl border-t border-x overflow-hidden"
           style={{
             background: 'linear-gradient(180deg, rgba(15, 20, 25, 0.95) 0%, rgba(10, 15, 20, 0.98) 100%)',
             borderColor: 'rgba(255, 255, 255, 0.12)',
             boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
           }}>
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 pt-6 pb-4 backdrop-blur-xl border-b"
             style={{
               background: 'rgba(15, 20, 25, 0.7)',
               borderColor: 'rgba(255, 255, 255, 0.1)',
             }}>
          <div className="flex items-center justify-between mb-3">
            <h2 style={{ color: '#FFFFFF' }}>Реєстрація</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-xl flex items-center justify-center border backdrop-blur-xl transition-all hover:border-white/30"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                }}
              >
                <X className="w-5 h-5" style={{ color: '#FFFFFF' }} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${(step / 7) * 100}%`,
                  background: 'linear-gradient(90deg, #00FF88 0%, #8B5CF6 100%)',
                }}
              />
            </div>
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
              Крок {step}/7
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ height: 'calc(90vh - 200px)' }}>
          {renderStep()}
          
          {errors && (
            <div className="p-3 rounded-xl border mb-4"
                 style={{
                   background: 'rgba(255, 0, 0, 0.1)',
                   borderColor: 'rgba(255, 0, 0, 0.3)',
                 }}>
              <p style={{ color: 'rgba(255, 100, 100, 0.9)', fontSize: '13px' }}>
                {errors}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 p-6 backdrop-blur-xl border-t"
             style={{
               background: 'rgba(15, 20, 25, 0.9)',
               borderColor: 'rgba(255, 255, 255, 0.1)',
             }}>
          <button
            onClick={validateAndNext}
            className="w-full h-14 rounded-2xl border backdrop-blur-xl transition-all hover:shadow-[0_0_30px_rgba(0,255,136,0.3)]"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 255, 136, 0.1) 100%)',
              borderColor: 'rgba(0, 255, 136, 0.5)',
              color: '#00FF88',
              boxShadow: '0 4px 20px rgba(0, 255, 136, 0.2), inset 0 2px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            {step === 3 ? 'Готово' : step === 7 ? 'Зберегти' : 'Далі'}
          </button>
        </div>
      </div>
    </div>
  );
}


